import type { RefObject } from 'react';
import type { Media } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

export interface DragState {
  isDragging: boolean;
  draggedIndex: number | null;
  dragOverIndex: number | null;
}

export interface MediaPickerOrderedStripProps {
  media: Media[];
  dragState: DragState;
  dragItemRef: RefObject<HTMLDivElement>;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onRemove: (media: Media) => void;
}

export const MediaPickerOrderedStrip = ({
  media,
  dragState,
  dragItemRef,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onRemove,
}: MediaPickerOrderedStripProps) => (
  <div className="p-6 border-b border-gray-200 flex-shrink-0">
    <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Images (drag to reorder)</h4>
    <div className="flex flex-wrap gap-3">
      {media.map((item, index) => (
        <div
          key={item.id}
          ref={index === dragState.draggedIndex ? dragItemRef : null}
          draggable
          onDragStart={(e) => onDragStart(e, index)}
          onDragOver={(e) => onDragOver(e, index)}
          onDrop={(e) => onDrop(e, index)}
          onDragEnd={onDragEnd}
          className={`relative group cursor-move ${
            dragState.isDragging && dragState.draggedIndex === index ? 'opacity-50' : ''
          } ${dragState.dragOverIndex === index ? 'ring-2 ring-primary-500' : ''}`}
        >
          <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
            <ImgWithSkeleton
              src={item.cloudinaryUrl}
              alt={item.filename}
              className="w-full h-full object-cover"
              skeletonClassName="rounded-lg"
            />
          </div>

          {/* Order indicator */}
          <div className="absolute -top-2 -left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {index + 1}
          </div>

          {/* Remove button */}
          <button
            onClick={() => onRemove(item)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ×
          </button>

          {/* Drag handle */}
          <div className="absolute bottom-1 right-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        </div>
      ))}
    </div>
  </div>
);
